import sessionApi, { listResults } from './apiClient';
import { ARE, Firearm, Issuance, MultiIssuanceAttempt, MultiIssuanceCase } from '../types/domain';

export interface IssuancePayload {
  firearm: number;
  officer: number;
  notes?: string;
  multiple_handgun_authority_claimed?: boolean;
  authority_reference?: string;
  authorization_document?: File;
  authorization_remarks?: string;
}

type AdminDepartmentFilter = {
  admin_department?: number;
  group?: 'open' | 'history' | 'all';
};

export const issuanceService = {
  getIssuances: async (filters: AdminDepartmentFilter = {}): Promise<Issuance[]> => {
    const response = await sessionApi.get('/issuances/', { params: filters });
    return listResults<Issuance>(response.data);
  },

  getEligibleFirearms: async (filters: AdminDepartmentFilter = {}): Promise<Firearm[]> => {
    const response = await sessionApi.get('/issuances/eligible-firearms/', { params: filters });
    return listResults<Firearm>(response.data);
  },

  createIssuance: async (payload: IssuancePayload | FormData): Promise<Issuance> => {
    let requestPayload: IssuancePayload | FormData = payload;
    if (!(typeof FormData !== 'undefined' && payload instanceof FormData) && (payload as IssuancePayload).authorization_document) {
      const formData = new FormData();
      Object.entries(payload as IssuancePayload).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        const isFile = typeof File !== 'undefined' && value instanceof File;
        formData.append(key, isFile ? value : String(value));
      });
      requestPayload = formData;
    }
    const isMultipart = typeof FormData !== 'undefined' && requestPayload instanceof FormData;
    const response = await sessionApi.post(
      '/issuances/',
      requestPayload,
      isMultipart ? { headers: { 'Content-Type': undefined } } : undefined
    );
    return response.data;
  },

  checkIssuancePolicy: async (params: { firearm_id: number; officer_id: number }): Promise<{
    same_firearm_conflict: boolean;
    same_firearm_authoritative_issuance_id?: number | null;
    multiple_handgun_review_required: boolean;
    authorization_supported: boolean;
    existing_active_handguns: Array<{
      issuance_id: number;
      firearm_id: number;
      firearm_serial_number: string;
      firearm_article: string;
      issued_at?: string | null;
    }>;
  }> => {
    const response = await sessionApi.get('/issuances/policy-check/', { params });
    return response.data;
  },

  getMultiIssuanceCases: async (filters: AdminDepartmentFilter = {}): Promise<MultiIssuanceCase[]> => {
    const response = await sessionApi.get('/multi-issuance-cases/', { params: filters });
    return listResults<MultiIssuanceCase>(response.data);
  },

  getMultiIssuanceAttempts: async (filters: { admin_department?: number } = {}): Promise<MultiIssuanceAttempt[]> => {
    const response = await sessionApi.get('/multi-issuance-attempts/', { params: filters });
    return listResults<MultiIssuanceAttempt>(response.data);
  },

  recordMultiIssuanceAttempt: async (payload: {
    firearm: number; attempted_officer: number; notes: string;
  }): Promise<MultiIssuanceAttempt> => {
    const response = await sessionApi.post('/multi-issuance-attempts/', payload);
    return response.data;
  },

  resolveMultiIssuanceCase: async (id: number, payload: {
    resolution_type: 'retain_existing' | 'refer_custody_correction' | 'false_positive';
    resolution_notes: string;
  }): Promise<MultiIssuanceCase> => {
    const response = await sessionApi.post(`/multi-issuance-cases/${id}/resolve/`, payload);
    return response.data;
  },

  approveIssuance: async (id: number, payload: { remarks?: string } = {}): Promise<Issuance> => {
    const response = await sessionApi.post(`/issuances/${id}/approve/`, payload);
    return response.data;
  },

  rejectIssuance: async (id: number, payload: { rejection_reason?: string } = {}): Promise<Issuance> => {
    const response = await sessionApi.post(`/issuances/${id}/reject/`, payload);
    return response.data;
  },

  flagMultiIssuance: async (id: number, payload: { remarks?: string } = {}): Promise<Issuance> => {
    const response = await sessionApi.post(`/issuances/${id}/flag-multi-issuance/`, payload);
    return response.data;
  },

  resolveMultiIssuance: async (id: number, payload: { remarks?: string } = {}): Promise<Issuance> => {
    const response = await sessionApi.post(`/issuances/${id}/resolve-multi-issuance/`, payload);
    return response.data;
  },

  returnIssuance: async (
    id: number,
    payload: {
      reason: string;
      firearm_condition: 'serviceable' | 'defective' | 'unserviceable';
      evidence_file: File;
      remarks?: string;
    }
  ) => {
    const formData = new FormData();
    formData.append('reason', payload.reason);
    formData.append('firearm_condition', payload.firearm_condition);
    formData.append('evidence_file', payload.evidence_file);
    if (payload.remarks?.trim()) formData.append('remarks', payload.remarks.trim());
    const response = await sessionApi.post(`/issuances/${id}/return/`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  revokeIssuance: async (id: number, payload: { reason: string; remarks?: string }) => {
    const response = await sessionApi.post(`/issuances/${id}/revoke/`, payload);
    return response.data;
  },

  confirmReturn: async (id: number): Promise<any> => {
    const response = await sessionApi.post(`/firearm-return-records/${id}/confirm/`);
    return response.data;
  },

  /**
   * Legacy/admin-only return rejection helper.
   *
   * Do not use this in the normal Logistics Return Oversight workflow.
   *
   * Phase 10 return is confirm-only:
   * Accountable submits return -> Logistics confirms viewed -> state changes.
   */
  rejectReturn: async (id: number, payload: { rejection_reason?: string } = {}): Promise<any> => {
    const response = await sessionApi.post(`/firearm-return-records/${id}/reject/`, payload);
    return response.data;
  },

  getAres: async (filters: AdminDepartmentFilter = {}): Promise<ARE[]> => {
    const response = await sessionApi.get('/ares/', { params: filters });
    return listResults<ARE>(response.data);
  },

  uploadAre: async (id: number, payload: FormData): Promise<ARE> => {
    const response = await sessionApi.post(`/ares/${id}/upload/`, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getAreDocument: async (id: number): Promise<Blob> => {
    const response = await sessionApi.get(`/ares/${id}/document/`, { responseType: 'blob' });
    return response.data;
  },

  getAuthorizationDocument: async (id: number): Promise<Blob> => {
    const response = await sessionApi.get(`/issuances/${id}/authorization-document/`, { responseType: 'blob' });
    return response.data;
  },

};
